// Settings-row + iOS-style switch primitives for the /flags page.
// Rows are fed as `childrenAs: 'state'` from the FEATURE_FLAGS registry,
// so `s.key/label/description/core/subs` is the flag def. Rows with
// `subs` expand ("detail down") into an indented SubFlagRow list —
// expansion lives on root._flagsOpen (session-only). The switch reads
// and writes through featureEnabled/setFeatureFlag — overrides persist
// to localStorage and mirror onto root._featureFlags, which every
// factory below reads, so toggles repaint reactively. Core flags render
// an "Always on" pill; their SUBS stay toggleable (parent locked,
// details tunable). A sub's switch disables while its parent is off.
import { shellFeatureEnabled, setFeatureFlag } from '../functions/featureFlags.js'

const _toggleOpen = (s) => {
  if (!(s.subs && s.subs.length)) return
  const root = s.root
  if (!root) return
  const cur = root._flagsOpen || {}
  root.update({ _flagsOpen: { ...cur, [s.key]: !cur[s.key] } })
}

// The flag-bound switch — visuals + geometry live in the generic
// SwitchInput (components/SwitchInput.js); this adds the /admin/flags
// registry semantics (featureEnabled read, setFeatureFlag write,
// parent-gated disable).
export const FlagSwitch = {
  extends: 'SwitchInput',
  onCheck: (el, s) => shellFeatureEnabled(s.root, s.key),
  'aria-label': (el, s) =>
    el
      .call('polyglot', 'admin.flags.toggleAria')
      .replace('{flag}', el.call('polyglot', s.label || s.key)),
  title: (el, s) =>
    el.call(
      'polyglot',
      shellFeatureEnabled(s.root, s.key) ? 'admin.flags.turnOff' : 'admin.flags.turnOn'
    ),
  // Sub-switches sleep while the parent feature is off — the parent gate
  // already forces them off, so flipping one would be a silent no-show.
  disabled: (el, s) => (s.parentKey && !shellFeatureEnabled(s.root, s.parentKey) ? true : undefined),
  onClick: (e, el, s) => {
    // The switch never doubles as the disclosure: one press, one flip.
    // `el` rides along so setFeatureFlag reaches the SDK (el.sdk) and
    // persists the flip to workspace.settings.featureFlags in Mongo.
    e.preventDefault()
    e.stopPropagation()
    setFeatureFlag(s.root, s.key, !shellFeatureEnabled(s.root, s.key), el)
  }
}

// Lock chip shown in place of the switch on `core: true` flags: the kit's
// tonal chip, neutral and dot-less (admin fix lane C, 2026-09-24 — it was
// 700 uppercase text at X2, about 10px, on the `overlay` wash). `status`
// is set so the chip never falls through to the flag def's own fields.
export const FlagAlwaysOnPill = {
  extends: 'AppStatusChip',
  status: 'neutral',
  label: 'admin.flags.alwaysOn',
  Dot: null,
  flexShrink: '0',
  title: (el) => el.call('polyglot', 'admin.flags.coreTitle')
}

// Disclosure chevron — only on rows that carry subs, and the ONE control
// that opens them: it toggles root._flagsOpen[key]. (A whole-row click used
// to toggle it too; on a row with no subs that handler did nothing and no
// keyboard reached it — admin fix lane C, 2026-09-24.)
export const FlagDisclosure = {
  tag: 'button',
  type: 'button',
  'aria-expanded': (el, s) => (s.root?._flagsOpen?.[s.key] ? 'true' : 'false'),
  'aria-label': (el, s) =>
    el
      .call('polyglot', 'admin.flags.disclosureAria')
      .replace('{flag}', el.call('polyglot', s.label || s.key)),
  // The compact control (controlHCompact square): it measured 38.5px from
  // its padding, neither control height.
  display: 'inline-flex',
  align: 'center center',
  minWidth: 'controlHCompact',
  minHeight: 'controlHCompact',
  padding: '0',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  round: 'radiusControl',
  color: 'caption',
  flexShrink: '0',
  ':hover': { background: 'hover', color: 'title' },
  onClick: (e, el, s) => {
    e.preventDefault()
    e.stopPropagation()
    _toggleOpen(s)
  },

  Icon: {
    name: 'arrowAngleDown',
    boxSize: 'Z2 Z2',
    transition: 'Z defaultBezier transform',
    transform: (el, s) => (s.root?._flagsOpen?.[s.key] ? 'rotate(180deg)' : 'none')
  }
}

// One sub-feature line — smaller type, same switch. While the parent feature
// is off, the SWITCH says so (it disables, the brand's disabled pair); the
// row's text stays at full ink — it was faded to opacity .55, which dragged
// the name and its description under 4.5:1.
export const SubFlagRow = {
  // Declared (as on FlagRow): the key is `<flag>.<sub>`, DATA.
  tag: 'div',
  flow: 'x',
  align: 'center space-between',
  gap: 'B',
  padding: 'Z1 0',
  // Row-separator-as-stroke, banned by design-direction.md §8 / DESIGN_
  // LANGUAGE §2.5 (mirrors SettingRow.js's identical fix) — each row's own
  // top+bottom `Z1` padding already spaces adjacent rows, so dropping the
  // line is geometry-preserving; the hover wash is the replacement cue.
  ':hover': { background: 'hover' },
  transition: 'Z defaultBezier background',

  Info: {
    flow: 'y',
    gap: 'W',
    minWidth: '0',

    // One level under its row's h3.
    Name: {
      tag: 'h4',
      margin: '0',
      fontSize: 'Z1',
      fontWeight: '700',
      color: 'title',
      text: (el, s) => el.call('polyglot', s.label || s.key)
    },
    Desc: {
      tag: 'p',
      margin: '0',
      fontSize: 'Y2',
      color: 'caption',
      lineHeight: '1.45',
      text: (el, s) => el.call('polyglot', s.description || '')
    }
  },

  FlagSwitch: {}
}

export const FlagRow = {
  // Declared, never inferred: the row key is the FLAG key, and DOMQL renders a
  // key named after an HTML tag as that tag — the `search` flag was a <search>
  // landmark, the `data` flag a <data> element (render census, 2026-09-24).
  tag: 'div',
  flow: 'y',
  padding: 'A 0',
  // Row-separator-as-stroke, banned by design-direction.md §8 / DESIGN_
  // LANGUAGE §2.5 (mirrors SettingRow.js's identical fix) — each row's own
  // top+bottom `A` padding already spaces adjacent rows, so dropping the
  // line is geometry-preserving.
  //
  // The row line is NOT a click target. It toggled the sub list on click,
  // which a keyboard never reached — and on a row without subs (a core flag:
  // the Always-on chip, no switch, no disclosure) the handler did nothing at
  // all (live census, 2026-09-24: "Profile & account", a click target no
  // keyboard reaches). The disclosure button is the one way to open the subs.
  //
  // NOT `Main`: DOMQL renders a key named after an HTML tag AS that tag, so
  // the row's line was a <main> landmark — 33 of them on /admin/flags (live
  // census, 2026-09-24), each one a "main content" stop for a screen reader.
  Top: {
    flow: 'x',
    align: 'center space-between',
    gap: 'B',

    Info: {
      flow: 'y',
      gap: 'V',
      minWidth: '0',

      // Under the group's h2 — h3 (it was h4, a skipped level on every row).
      Name: {
        tag: 'h3',
        margin: '0',
        fontSize: 'Z2',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => el.call('polyglot', s.label || s.key)
      },
      Desc: {
        tag: 'p',
        margin: '0',
        fontSize: 'Y2',
        color: 'caption',
        lineHeight: '1.45',
        text: (el, s) => el.call('polyglot', s.description || '')
      }
    },

    Right: {
      flow: 'x',
      align: 'center',
      gap: 'Z',
      flexShrink: '0',

      SubCount: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        whiteSpace: 'nowrap',
        hide: (el, s) => !(s.subs && s.subs.length),
        text: (el, s) => {
          const n = (s.subs || []).length
          return el
            .call('polyglot', n === 1 ? 'admin.flags.subCountOne' : 'admin.flags.subCount')
            .replace('{count}', n)
        }
      },

      FlagAlwaysOnPill: { hide: (el, s) => !s.core },
      FlagSwitch: { hide: (el, s) => !!s.core },
      FlagDisclosure: { hide: (el, s) => !(s.subs && s.subs.length) }
    }
  },

  // Sub-feature list — an inset plate inside the card reads as a level of
  // hierarchy. The neutral `widgetPlate` (it reads on a card; the `overlay`
  // wash it replaced composited to the card's own colour).
  SubList: {
    hide: (el, s) => !(s.subs && s.subs.length) || !s.root?._flagsOpen?.[s.key],
    flow: 'y',
    marginTop: 'A',
    background: 'widgetPlate',
    round: 'radiusCard',
    padding: 'Y A2',
    childExtends: 'SubFlagRow',
    childrenAs: 'state',
    children: (el, s) =>
      (s.subs || []).map((sub) => ({
        ...sub,
        key: `${s.key}.${sub.key}`,
        parentKey: s.key
      }))
  }
}

// One flag GROUP — head (label + description) plus the card of FlagRows.
// Mounted via `childExtends: 'FlagGroupSection'` + `childrenAs: 'state'` over
// `featureFlagGroupsWithFlags()` (functions/featureFlags.js); state shape is
// the registry group `{ key, label, description }` with its `flags` attached.
// Nested factories read `s.<field>` directly — state cascades through plain
// (non-childrenAs) nested children, same as ReminderGroupSection above.
//
// This used to be a module-local `_groupBlock(group)` factory in
// pages/flags.js, mapped at MODULE scope. That single line made the entire
// workspace-shell project uncompilable by frank (tickets/fable.md, "most
// workspace packages cannot be compiled by frank at all"): frank stubs bare
// `@symbo-ls/*` specifiers by design, so the registry was `undefined` at
// build time and `undefined.map` threw while the bundle was still loading.
// A registered component fed by a render-time factory has no module-scope
// evaluation to fail, and — unlike a module-local helper, which frank drops
// — it survives serialization.
export const FlagGroupSection = {
  // Declared (as on FlagRow): the section key is the registry GROUP key.
  tag: 'div',
  flow: 'y',
  gap: 'A',

  Head: {
    flow: 'y',
    gap: 'V',

    // The section heading — one step under the page title (it was B1, larger
    // than the page's own h1).
    GroupTitle: {
      extends: 'AdminCardTitle',
      text: (el, s) => el.call('polyglot', s.label || '')
    },
    GroupCaption: {
      tag: 'p',
      margin: '0',
      fontSize: 'Z',
      color: 'caption',
      text: (el, s) => el.call('polyglot', s.description || '')
    }
  },

  // The admin section card: the branded `card` step off the sheet —
  // CommonCard is the sheet's OWN fill (an invisible card).
  Card: {
    extends: 'AdminCard',
    gap: '0',
    padding: 'Y B',
    childExtends: 'FlagRow',
    childrenAs: 'state',
    children: (el, s) => s.flags || []
  }
}
